import { OCR_MIME_TYPES } from '../ocr/ocr';
import { UPLOAD_LIMIT_LABEL } from '../files/classification';
import { CERTIFICATE_UPLOAD_MAX_BYTES } from '../records/certificateRequest';
import { byteText } from '../catalog/itemImage';

/**
 * Document reading (piece 15; decision 0347): the Google Cloud Vision key
 * the OcrProvider seam opens for a scanned or photographed certificate,
 * entered once on Settings › Documents › Document Reading and held sealed
 * in the KeyProvider envelope beside the Maps key. 🔨 No key held means no
 * vendor reads a picture; the seam answers unread and the person types.
 */
export const OCR_GOOGLE_KEY_KEY = 'ocr.googleApiKey';

/**
 * 🔒 The reading order Settings › Documents › Document Reading states (T212;
 * decision 0701): a PDF's own form fields, then a PDF's text layer or a
 * plain-text file's bytes, then a picture through the `OcrProvider` seam, which
 * reads only with a Google Cloud Vision key held (decisions 0347, 0678). The
 * first step that reads wins. 🔨 The order is the reader's own and is shown, not
 * edited: it is not a Settings key and no screen changes it.
 */
export const DOCUMENT_READING_STEPS = ['fields', 'text', 'picture'] as const;
export type DocumentReadingStep = (typeof DOCUMENT_READING_STEPS)[number];

/** Each picture kind the seam reads, named the way a person names the file. */
const PICTURE_KIND_NAMES: Readonly<Record<string, string>> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/gif': 'GIF',
  'image/bmp': 'BMP',
  'image/tiff': 'TIFF',
};

/**
 * 🔒 The file kinds a document is read from (decision 0701): a PDF and a
 * plain-text file with no key, and every picture kind the OCR seam takes,
 * read off `OCR_MIME_TYPES` so the page cannot name a kind the reader refuses.
 */
export function documentReadingKinds(): readonly string[] {
  return ['PDF', 'plain text', ...OCR_MIME_TYPES.map((mime) => PICTURE_KIND_NAMES[mime] ?? mime)];
}

/**
 * 🔒 The size caps a document meets before it is read (decision 0701): the upload
 * limit every file meets, and the smaller cap on a certificate a customer sends
 * through its one-time link. Both are constants, shown and not edited.
 */
export const DOCUMENT_READING_CAPS = {
  upload: UPLOAD_LIMIT_LABEL,
  publicCertificate: byteText(CERTIFICATE_UPLOAD_MAX_BYTES),
} as const;
